/**
 * 防重复执行 Hook
 *
 * 使用方式：
 * 1. 【必须】在 App.tsx 中调用 initializeDuplicatePrevent() 初始化（已完成）
 * 2. 【必须】在 App.tsx 的 beforeDestroy 中调用 destroyDuplicatePrevent()（已完成）
 * 3. 在页面组件中使用 getDuplicatePrevent() 或 useDuplicatePrevent() 获取实例
 *
 * 示例：
 * ```typescript
 * // 在 Class Component 中
 * import { getDuplicatePrevent } from '@/hooks/useDuplicatePrevent';
 * const duplicatePrevent = getDuplicatePrevent();
 *
 * // 在 Composition API 组件中
 * import { useDuplicatePrevent } from '@/hooks/useDuplicatePrevent';
 * const duplicatePrevent = useDuplicatePrevent();
 *
 * // 使用
 * const handleClick = duplicatePrevent.execute(async () => {
 *   await someAsyncOperation();
 * });
 * ```
 */

import router from '@/router';
import { createDuplicatePrevent } from '@hb/duplicate-prevent';
import type { IDuplicatePrevent } from '@hb/duplicate-prevent';

// 全局单例实例
let globalDuplicatePrevent: IDuplicatePrevent | null = null;
// 路由守卫是否已初始化
let isRouterGuardInitialized = false;

/**
 * 获取全局 duplicate-prevent 实例
 * 单例模式，确保全项目使用同一个实例
 */
export function getDuplicatePrevent(): IDuplicatePrevent {
  if (!globalDuplicatePrevent) {
    globalDuplicatePrevent = createDuplicatePrevent();
  }
  return globalDuplicatePrevent;
}

/**
 * 初始化路由守卫
 * 在 App.tsx 的 created 或 mounted 中调用
 */
export function initializeDuplicatePrevent(): void {
  if (isRouterGuardInitialized) return;

  const duplicatePrevent = getDuplicatePrevent();

  // 页面跳转时清理可中止任务
  router.beforeEach((to, from, next) => {
    // 调用 abortAll() 清理可中止任务
    // 设置了 abortable: false 的关键任务不会被影响
    duplicatePrevent.abortAll();
    // 必须调用 next() 继续路由导航
    next();
  });

  isRouterGuardInitialized = true;
}

/**
 * 销毁 duplicate-prevent 实例
 * 在 App.tsx 的 beforeDestroy 中调用
 */
export function destroyDuplicatePrevent(): void {
  if (globalDuplicatePrevent) {
    // 强制清理所有任务（包括 abortable: false 的任务）
    globalDuplicatePrevent.forceAbortAll();
    globalDuplicatePrevent = null;
  }
  isRouterGuardInitialized = false;
}

/**
 * Composable Hook（可选）
 * 用于 Composition API 组件中使用
 * 注意：由于全局已在 App.tsx 中初始化，此函数主要用于获取实例
 */
export function useDuplicatePrevent(): IDuplicatePrevent {
  return getDuplicatePrevent();
}

export default {
  getDuplicatePrevent,
  initializeDuplicatePrevent,
  destroyDuplicatePrevent,
  useDuplicatePrevent,
};
